import styled, { css, keyframes } from "styled-components";
import { fonts } from "../../styles/theme";

export const cores = {
  blue: "#0B57D0",
  blueDark: "#0842A0",
  blueTint: "#E8F0FE",
  blueTint2: "#D3E3FD",
  green: "#137333",
  greenTint: "#E6F4EA",
  yellowTint: "#FEF7E0",
  amber: "#A85F00",
  red: "#B3261E",
  redTint: "#FCE8E6",
  grayTint: "#F8F9FA",
  ink: "#202124",
  ink2: "#3C4043",
  ink3: "#5F6368",
  ink4: "#80868B",
  line: "#E8EAED",
  dark: "#202124",
};

export const ease = "cubic-bezier(.25,1,.5,1)";

const entrada = keyframes`
  to { opacity: 1; transform: none; }
`;

const crescer = keyframes`
  to { transform: scaleX(1); }
`;

const destacar = keyframes`
  to { background-size: 100% 100%; }
`;

export const revelar = (atraso = 0) => css`
  opacity: 0;
  transform: translateY(18px);
  animation: ${entrada} 0.8s ${ease} ${atraso}s forwards;
  @media (prefers-reduced-motion: reduce), print {
    animation: none;
    opacity: 1;
    transform: none;
  }
`;

export const Pagina = styled.main`
  font-family: ${fonts.gilroyRegular}, system-ui, -apple-system, Arial, sans-serif;
  color: ${cores.ink};
  background: #ffffff;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;

  h1,
  h2,
  h3 {
    font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.02em;
  }

  a {
    color: ${cores.blue};
    text-decoration: none;
  }
`;

export const Largura = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 20px;

  @media (min-width: 900px) {
    padding: 0 32px;
  }
`;

export const Topo = styled.header<{ $atraso?: number }>`
  ${({ $atraso }) => revelar($atraso ?? 0.05)}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 0;

  img {
    height: 34px;
    width: auto;
    @media (min-width: 900px) {
      height: 40px;
    }
  }

  .acoes {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .so-desktop {
    display: none;
    @media (min-width: 900px) {
      display: inline-flex;
    }
  }
`;

export const Secao = styled.section<{ $atraso?: number }>`
  ${({ $atraso }) => revelar($atraso ?? 0)}
  padding: 36px 0;

  @media (min-width: 900px) {
    padding: 56px 0;
  }
`;

export const Faixa = styled.section<{ $cor: "azul" | "verde" | "amarela" | "vermelha" | "escura"; $atraso?: number }>`
  ${({ $atraso }) => revelar($atraso ?? 0)}
  padding: 36px 0;
  background: ${({ $cor }) =>
    ({ azul: cores.blueTint, verde: cores.greenTint, amarela: cores.yellowTint, vermelha: cores.redTint, escura: cores.dark })[$cor]};
  color: ${({ $cor }) => ($cor === "escura" ? "#ffffff" : cores.ink)};

  @media (min-width: 900px) {
    padding: 72px 0;
  }
`;

export const Titulo = styled.h2`
  font-size: 24px;
  line-height: 1.25;

  @media (min-width: 900px) {
    font-size: 32px;
    line-height: 1.2;
  }
`;

export const Apoio = styled.p<{ $claro?: boolean }>`
  font-size: 14px;
  line-height: 1.6;
  margin: 6px 0 0;
  color: ${({ $claro }) => ($claro ? "#BDC1C6" : cores.ink2)};

  @media (min-width: 900px) {
    font-size: 17px;
  }
`;

export const Meta = styled.span<{ $claro?: boolean }>`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ $claro }) => ($claro ? "#BDC1C6" : cores.ink4)};

  @media (min-width: 900px) {
    font-size: 13px;
  }
`;

export const Card = styled.div<{ $fundo?: string; $compacto?: boolean }>`
  background: ${({ $fundo }) => $fundo ?? "#ffffff"};
  border-radius: 24px;
  padding: ${({ $compacto }) => ($compacto ? "16px 20px" : "22px")};
  box-sizing: border-box;
  transition: transform 0.4s ${ease}, box-shadow 0.4s ease;

  @media (min-width: 900px) {
    border-radius: 28px;
    padding: ${({ $compacto }) => ($compacto ? "18px 24px" : "28px")};
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 28px rgba(60, 64, 67, 0.1);
    }
  }
`;

export const Bloco = styled.div<{ $cor?: string }>`
  background: ${({ $cor }) => $cor ?? cores.blueTint2};
  border-radius: 28px;
  padding: 22px;
  display: grid;
  gap: 14px;

  @media (min-width: 900px) {
    border-radius: 40px;
    padding: 32px;
    gap: 24px;
    grid-template-columns: 1.1fr 1fr;
  }
`;

export const Pilula = styled.a<{ $tipo?: "primaria" | "contorno" | "clara" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 1000px;
  font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
  font-size: 15px;
  padding: 13px 26px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.3s ${ease}, box-shadow 0.3s ease;
  ${({ $tipo }) =>
    $tipo === "contorno"
      ? css`
          border: 1px solid #dadce0;
          background: #ffffff;
          && {
            color: ${cores.blue};
          }
          &:hover {
            background: ${cores.grayTint};
          }
        `
      : $tipo === "clara"
        ? css`
            background: #ffffff;
            && {
              color: ${cores.ink};
            }
          `
        : css`
            background: ${cores.blue};
            && {
              color: #ffffff;
            }
            &:hover {
              background: ${cores.blueDark};
              transform: translateY(-1px);
              box-shadow: 0 6px 16px rgba(11, 87, 208, 0.24);
            }
          `}
`;

export const Etiqueta = styled.span<{ $cor?: "cinza" | "amarela" | "verde" | "azul" | "vermelha" | "clara" }>`
  display: inline-flex;
  align-items: center;
  border-radius: 1000px;
  font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
  font-size: 11px;
  padding: 4px 10px;
  white-space: nowrap;
  ${({ $cor }) => {
    switch ($cor) {
      case "amarela":
        return css`background: ${cores.yellowTint}; color: ${cores.amber};`;
      case "verde":
        return css`background: ${cores.greenTint}; color: ${cores.green};`;
      case "azul":
        return css`background: ${cores.blueTint}; color: ${cores.blue};`;
      case "vermelha":
        return css`background: ${cores.redTint}; color: ${cores.red};`;
      case "clara":
        return css`background: rgba(255, 255, 255, 0.14); color: #ffffff;`;
      default:
        return css`background: ${cores.grayTint}; color: ${cores.ink3};`;
    }
  }}
`;

export const Chips = styled.div`
  display: flex;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Chip = styled.button<{ $ativo?: boolean }>`
  padding: 9px 16px;
  border-radius: 1000px;
  border: 1px solid ${({ $ativo }) => ($ativo ? cores.ink : cores.line)};
  font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
  font-size: 13px;
  color: ${({ $ativo }) => ($ativo ? "#ffffff" : cores.ink3)};
  background: ${({ $ativo }) => ($ativo ? cores.ink : "#ffffff")};
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;
`;

export const Numero = styled.span<{ $tamanho?: number; $cor?: string }>`
  font-family: ${fonts.gilroyBold}, system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  line-height: 1;
  font-size: ${({ $tamanho }) => $tamanho ?? 42}px;
  color: ${({ $cor }) => $cor ?? cores.ink};
`;

export const Destaque = styled.span<{ $cor?: "azul" | "verde"; $atraso?: number }>`
  display: inline-block;
  font-family: ${fonts.gilroyBold}, system-ui, sans-serif;
  font-size: 1.12em;
  color: ${({ $cor }) => ($cor === "verde" ? cores.green : cores.blue)};
  padding: 0 7px;
  border-radius: 9px;
  background: linear-gradient(
      ${({ $cor }) => ($cor === "verde" ? cores.greenTint : cores.blueTint2)},
      ${({ $cor }) => ($cor === "verde" ? cores.greenTint : cores.blueTint2)}
    )
    no-repeat 0 100% / 0% 100%;
  animation: ${destacar} 0.9s ${ease} ${({ $atraso }) => $atraso ?? 0.7}s forwards;

  @media (prefers-reduced-motion: reduce), print {
    animation: none;
    background-size: 100% 100%;
  }
`;

export const Posicao = styled.span<{ $topo?: boolean }>`
  min-width: 34px;
  height: 26px;
  border-radius: 1000px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  padding: 0 8px;
  flex-shrink: 0;
  background: ${({ $topo }) => ($topo ? cores.greenTint : cores.grayTint)};
  color: ${({ $topo }) => ($topo ? cores.green : cores.ink3)};
`;

export const Linha = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 14px 0;
  border-top: 1px solid ${cores.line};

  &:first-child {
    border-top: none;
  }

  .texto {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  .nome {
    font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
    font-size: 15px;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }
`;

export const Barra = styled.div<{ $largura: number; $atraso?: number; $cor?: string }>`
  height: 6px;
  border-radius: 3px;
  background: ${cores.grayTint};
  overflow: hidden;

  &::after {
    content: "";
    display: block;
    height: 100%;
    width: ${({ $largura }) => Math.max(2, Math.min(100, $largura))}%;
    background: ${({ $cor }) => $cor ?? cores.blue};
    border-radius: 3px;
    transform-origin: left;
    transform: scaleX(0);
    animation: ${crescer} 1.1s ${ease} ${({ $atraso }) => $atraso ?? 0.5}s forwards;
  }

  @media (prefers-reduced-motion: reduce), print {
    &::after {
      animation: none;
      transform: none;
    }
  }
`;

export const Grade = styled.div<{ $colunas?: number; $desktop?: number }>`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(${({ $colunas }) => $colunas ?? 2}, minmax(0, 1fr));

  @media (min-width: 900px) {
    gap: 20px;
    grid-template-columns: repeat(${({ $desktop, $colunas }) => $desktop ?? $colunas ?? 2}, minmax(0, 1fr));
  }
`;

export const DuasColunas = styled.div<{ $proporcao?: string }>`
  display: grid;
  gap: 18px;

  @media (min-width: 900px) {
    gap: 64px;
    align-items: center;
    grid-template-columns: ${({ $proporcao }) => $proporcao ?? "1fr 1.3fr"};
  }
`;

export const Centro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;

  h1 {
    font-size: 32px;
    line-height: 1.18;
    max-width: 860px;

    @media (min-width: 900px) {
      font-size: 56px;
      line-height: 1.1;
      letter-spacing: -0.03em;
    }
  }

  .frase {
    font-size: 17px;
    line-height: 1.85;
    margin: 0;
    color: ${cores.ink2};
    max-width: 760px;

    @media (min-width: 900px) {
      font-size: 20px;
      line-height: 1.9;
    }
  }
`;

export const Aviso = styled.div<{ $cor?: "amarela" | "azul" }>`
  border-radius: 20px;
  padding: 18px 20px;
  background: ${({ $cor }) => ($cor === "amarela" ? cores.yellowTint : cores.blueTint)};
  color: ${({ $cor }) => ($cor === "amarela" ? cores.amber : cores.blue)};
  font-family: ${fonts.gilroySemiBold}, system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

export const Esqueleto = styled.div<{ $altura?: number; $largura?: string }>`
  height: ${({ $altura }) => $altura ?? 16}px;
  width: ${({ $largura }) => $largura ?? "100%"};
  border-radius: 12px;
  background: linear-gradient(90deg, #eef0f3, #e4e7eb, #eef0f3);
  background-size: 200% 100%;
`;

export const Rodape = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 0 36px;

  img {
    height: 30px;
    width: auto;
  }

  @media (min-width: 900px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

export const BarraFixa = styled.div`
  position: sticky;
  bottom: 0;
  background: #ffffff;
  border-top: 1px solid ${cores.line};
  padding: 14px 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 -8px 24px rgba(60, 64, 67, 0.08);
  z-index: 5;

  @media (min-width: 900px), print {
    display: none;
  }
`;
